import { z } from 'zod';
import type { MoneyJson } from '../money/money';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * The parts fitted register (gitaumoses4/chukua#174): which part went on which truck, when, at what odometer, from
 * whom and on which work order. It is a read over the effective parts lines of work orders that have started, so a
 * correction or retraction on the order shows here at once. Warranty checks are #175's and are not here.
 */

const name = z.string().trim().min(1).max(100);
const expectedLife = (max: number) => z.number().int().min(1).max(max).nullable();

/** `POST /part-types`. The code is the lowercased name, as with the directory tags. */
export const partTypeInputSchema = z
  .object({
    name,
    /** A replacement sooner than this many km on the same truck is early failure. */
    expectedLifeKm: expectedLife(10_000_000).optional(),
    expectedLifeDays: expectedLife(36_500).optional(),
  })
  .strict();
export type PartTypeInput = z.infer<typeof partTypeInputSchema>;

/** `PATCH /part-types/:id`. A threshold left out is kept; an explicit null clears it. */
export const partTypeUpdateSchema = partTypeInputSchema.partial().strict();
export type PartTypeUpdate = z.infer<typeof partTypeUpdateSchema>;

export const partTypeListQuerySchema = defineListQuery({
  sortFields: ['name', 'expectedLifeKm', 'expectedLifeDays'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: {
    isActive: z
      .enum(['true', 'false'])
      .transform((value) => value === 'true')
      .optional(),
  },
});
export type PartTypeListQuery = z.output<typeof partTypeListQuerySchema>;

export interface PartTypeView {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  expectedLifeKm: number | null;
  expectedLifeDays: number | null;
}

/** One part fitted: an effective parts line on a work order that has started, with the order's truck, date and odometer. */
export interface PartFittingView {
  /** The parts line's id. */
  id: string;
  workOrderId: string;
  workOrderNumber: number;
  truckId: string;
  truckRegistration: string | null;
  partTypeId: string | null;
  partTypeName: string | null;
  part: string;
  partNumber: string | null;
  serial: string | null;
  quantity: number;
  unitCost: MoneyJson;
  supplierId: string | null;
  supplierName: string | null;
  /** The order's completion, else its start. */
  fittedAt: string;
  /** From the order's newest details; null when none was recorded. */
  odometerKm: number | null;
}

const registerFilters = {
  truckId: listFilter(z.uuid()),
  partTypeId: listFilter(z.uuid()),
  supplierId: listFilter(z.uuid()),
  fittedFrom: z.iso.date().optional(),
  fittedTo: z.iso.date().optional(),
};
const registerSortFields = ['fittedAt', 'odometerKm', 'partTypeName', 'unitCost', 'workOrderNumber'] as const;

/** `GET /parts-register` — search matches part, part number, serial, work order, supplier and truck registration. */
export const partsRegisterListQuerySchema = defineListQuery({
  sortFields: registerSortFields,
  defaultSort: { sort: 'fittedAt', order: 'desc' },
  filters: registerFilters,
});
export type PartsRegisterListQuery = z.output<typeof partsRegisterListQuerySchema>;

/** `GET /trucks/:id/parts` — the register scoped to one truck, so no `truckId` filter. */
export const truckPartsListQuerySchema = defineListQuery({
  sortFields: registerSortFields,
  defaultSort: { sort: 'fittedAt', order: 'desc' },
  filters: {
    partTypeId: registerFilters.partTypeId,
    supplierId: registerFilters.supplierId,
    fittedFrom: registerFilters.fittedFrom,
    fittedTo: registerFilters.fittedTo,
  },
});
export type TruckPartsListQuery = z.output<typeof truckPartsListQuerySchema>;

/** `GET /trucks/:id/part-intervals` — every part type the truck has had replaced, or the ones asked for. */
export const partIntervalListQuerySchema = defineListQuery({
  sortFields: ['fittedAt', 'partTypeName', 'km', 'days'],
  defaultSort: { sort: 'fittedAt', order: 'desc' },
  filters: {
    partTypeId: listFilter(z.uuid()),
    isShort: z
      .enum(['true', 'false'])
      .transform((value) => value === 'true')
      .optional(),
  },
});
export type PartIntervalListQuery = z.output<typeof partIntervalListQuerySchema>;

/** A part type replaced on a truck: how long and how far the one it replaced lasted. */
export interface PartReplacementIntervalView {
  partTypeId: string;
  partTypeName: string;
  /** The type's expected life, as the interval was judged against it. */
  expectedLifeKm: number | null;
  expectedLifeDays: number | null;
  /** The earlier fitting, and the one that replaced it. */
  replacedPartsLineId: string;
  replacedByPartsLineId: string;
  replacedFittedAt: string;
  replacedByFittedAt: string;
  days: number;
  /** Null when either odometer is unknown or the later one is lower. */
  km: number | null;
  /** Under the type's expected life in km or days. Never true when the type sets neither. */
  isShort: boolean;
}
